"use client";

import { useRef } from "react";
import { Eyebrow, SectionShell, Terminal, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger, useMaskedReveal } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import { APPSEC } from "./data";

/**
 * Application security — one fact per line in a dark code pane; the
 * implementation depth lives on /developers, which every row links to.
 */
export default function AppSecuritySection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });
  useEnterStagger(rootRef, "[data-sec-pane]", { y: 26 });

  return (
    <SectionShell className="md:pt-px md:pb-px">
      <div ref={rootRef}>
        {/* One block — claim and pane share a single clearing. */}
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[5fr_7fr] lg:gap-16"
        >
          <div ref={copyRef} className="min-w-0">
            <div data-head-sub className="flex">
              <Eyebrow>Application security</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em]">
              The parts your pentester greps for.
            </h2>
            <p
              data-head-sub
              className="mt-[18px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted"
            >
              One fact per line — the implementation detail lives on /developers.
            </p>
          </div>

          <div data-sec-pane className="min-w-0">
            <Terminal path="rii/app — security facts">
              <div className="flex flex-col gap-3.5 text-[12.5px] leading-[1.6]">
                {APPSEC.map((row) => (
                  <div
                    key={row.k}
                    className="grid items-baseline gap-x-3.5 gap-y-0.5 sm:grid-cols-[96px_minmax(0,1fr)_auto]"
                  >
                    <span className="text-data-cyan">{row.k}</span>
                    <span className="text-[#e8e8f0]">{row.fact}</span>
                    <a
                      href={routePath("developers")}
                      className="whitespace-nowrap text-brand-400 underline-offset-2 hover:underline max-sm:hidden"
                    >
                      docs →
                    </a>
                  </div>
                ))}
                <div className="mt-1 text-[#565668]">
                  # no passwords in api calls · keys revocable instantly, self-serve
                </div>
              </div>
            </Terminal>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
